import {
  BrandTypeAndSalesStatisticResponse,
  ConnectPOSDto,
  ConnectProviderRequestData,
  CreateCompleteLocation,
  CreateStaffMemberRequestData,
  CreateWorkingHoursRequestData,
  DashboardResponse,
  GetLocationsOverviewParams,
  IncompleteLocation,
  LocationAgent,
  LocationAgentServiceKey,
  LocationAgentsEarningPlans,
  LocationEarningPlan,
  LocationTrackingSmsEntity,
  LocationVerificationCountResponse,
  SaveLocationAgentSharesRequestData,
  PosStatusResponse,
  PosType,
  PreviewReceiptSizeResponse,
  PrintingSettingsRequestData,
  PrintingSettingsResponse,
  ProviderAndConnectionStatusResponse,
  ProviderAndProviderStatusResponse,
  ServiceFeeSuggestion,
  SetLocationCredentialsRequestData,
  SimpleLocationWithTodaysDataResponse,
  UpdateLocationConfigRequestData,
  UpdateLocationEarningsPlanRequestData,
  UpdateLocationOwnershipRequestData,
  UpdateLocationRequestData,
  UpdateLocationSalesRequestData,
  TrackingSmsType,
  UpdatePayoutConfigsRequestData,
  UpsertProviderContactRequestData,
  VerifyLocationRequestData,
  ProviderPauseLength,
  ScheduledPause,
} from '../types/api/location.types';
import {
  CreateMenuProvider,
  MenuEntity,
  MenuSignInPropertyEntity,
} from '../types/api/menu.types';
import {
  ActiveBrandEntity,
  OwnBrandEntity,
  Brand,
  BrandProviderConfigEntity,
} from '../types/brand.types';
import { OrdersOverviewEntity } from '../types/brandOwner';
import {
  HolidayHoursReponseV2,
  LocationEntity,
  LocationSettings,
  PayoutConfigResponse,
  SimpleLocationEntity,
  UpdateHolidayHoursLocationV2Dto,
  UpdateWorkingHoursLocationV2Dto,
  WorkingHoursReponseV2,
  AgentCustomerStatsResponse,
  AgentCustomersStatsVDriveResponse,
  AgentCustomersStatsRecoveryResponse,
  AgentCustomersStatsVGrubsResponse,
} from '../types/location.types';
import {
  DateRangeParams,
  PaginationParams,
  Provider,
  ResponseType,
} from '../types/shared.types';
import { api } from './api';

export const getAllVerifiedLocations = (
  params: PaginationParams<{
    query?: string | null;
    verified?: boolean;
    deleted?: boolean;
  }>
) => {
  return api.get<ResponseType<SimpleLocationEntity[]>>(
    '/v1/locations/verified',
    {
      params,
    }
  );
};

export const recoverLocationById = (locationId: string | number) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/recover`);

export const getAllVerifiedLocationsCount = (params: {
  query?: string | null;
}) =>
  api.get<ResponseType<{ count: number }>>('/v1/locations/verified/count', {
    params,
  });

export const getAllVerifiedLocationsCountAll = (params: {
  query?: string | null;
}) =>
  api.get<ResponseType<{ deletedCount: number; nonDeletedCount: number }>>(
    '/v1/locations/verified/count-all',
    {
      params,
    }
  );

export const getAllLocationsWithRecoveryStats = (
  params: PaginationParams<{
    verified: boolean;
    query?: string;
    to?: string;
  }>
) =>
  api.get<ResponseType<SimpleLocationWithTodaysDataResponse>>(
    `/v1/locations/recovery`,
    { params }
  );

export const getLocationVerificationCount = () =>
  api.get<ResponseType<LocationVerificationCountResponse>>(
    '/v1/locations/verification-count'
  );

export const getAllUnverifiedLocations = (
  params: PaginationParams<{ query?: string }>
) =>
  api.get<ResponseType<Array<SimpleLocationEntity>>>(
    '/v1/locations/unverified',
    {
      params,
    }
  );

export const getLocationDashboardData = (
  params: DateRangeParams<{
    locationIds?: Array<number | string>;
    brandIds?: Array<number | string>;
  }>
) =>
  api.get<ResponseType<DashboardResponse>>('/v1/locations/dashboard', {
    params,
  });

export const getDashboardBrandsData = (
  params: DateRangeParams<{
    locationIds?: Array<number | string>;
    brandIds?: Array<number | string>;
  }>
) =>
  api.get<ResponseType<BrandTypeAndSalesStatisticResponse[]>>(
    '/v1/locations/dashboard-brands',
    {
      params,
    }
  );

export const migrateAllBrandsOrders = (brandId: string | number) =>
  api.post<ResponseType<null>>(`/v1/locations/${brandId}/migrate-orders`);

export const uploadLocationsImage = (
  locationId: string | number,
  data: FormData
) => {
  api.post<ResponseType<string>>(`/v1/locations/${locationId}/image`, data, {
    headers: { 'content-type': 'multipart/form-data' },
  });
};

export const getLocationMenuItems = (locationId: string | number) =>
  api.get<ResponseType<MenuEntity[]>>(`/v1/locations/${locationId}/menu`);

export const updateAutoAcceptPrepMinutes = (
  locationId: string | number,
  data: {
    prepTime: null | number;
  }
) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/auto-accept`, data);

export const updateLocationPrintingSettings = (
  locationId: string | number,
  data: PrintingSettingsRequestData
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/printing-settings`,
    data
  );

export const getCurrentFrontReceptSize = (locationId: string | number) =>
  api.get<ResponseType<PreviewReceiptSizeResponse>>(
    `/v1/locations/${locationId}/front-size`
  );

export const updateFrontReceptSize = (
  locationId: string | number,
  data: {
    value: number;
  }
) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/front-size`, data);

export const getCurrentBackReceptSize = (locationId: string | number) =>
  api.get<ResponseType<PreviewReceiptSizeResponse>>(
    `/v1/locations/${locationId}/back-size`
  );

export const updateBackReceptSize = (
  locationId: string | number,
  data: {
    value: number;
  }
) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/back-size`, data);

export const previewFrontSize = (
  locationId: string | number,
  data: {
    value: number;
  }
) =>
  api.post<ResponseType<PreviewReceiptSizeResponse>>(
    `/v1/locations/${locationId}/preview-front-size`,
    data
  );

export const previewBackSize = (
  locationId: string | number,
  data: {
    value: number;
  }
) =>
  api.post<ResponseType<PreviewReceiptSizeResponse>>(
    `/v1/locations/${locationId}/preview-back-size`,
    data
  );

export const updatePrintingSettingsWithImage = (
  locationId: string | number,
  data: FormData
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/printing-settings-image`,
    data,
    {
      headers: { 'content-type': 'multipart/form-data' },
    }
  );

export const deleteFooterImage = (locationId: string | number) =>
  api.delete<ResponseType<null>>(`/v1/locations/${locationId}/footer-image`);

export const verifyLocation = (
  locationId: string | number,
  data: VerifyLocationRequestData
) => api.post<ResponseType<null>>(`/v1/locations/${locationId}/verify`, data);

export const rejectVerification = (
  locationId: string | number,
  reason: string
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/reject-verification`,
    { reason }
  );

export const deleteLocationById = (
  locationId: string | number,
  confirmUnpaidOrders?: boolean
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/delete`,
    confirmUnpaidOrders ? { confirmUnpaidOrders: true } : undefined
  );

export const getLocationSettings = (locationId: string | number) =>
  api.get<ResponseType<LocationSettings>>(
    `/v1/locations/${locationId}/settings`
  );

export const getLocationBrands = (locationId: string | number) =>
  api.get<ResponseType<Brand[]>>(`/v1/locations/${locationId}/brands`);

export const getLocationProviderStatuses = (
  locationId: string | number,
  config?: import('axios').AxiosRequestConfig
) =>
  api.get<ResponseType<ProviderAndProviderStatusResponse>>(
    `/v1/locations/${locationId}/providers-statuses`,
    config
  );

export const getLocationConnectionStatus = (locationId: string | number) =>
  api.get<ResponseType<ProviderAndConnectionStatusResponse>>(
    `/v1/locations/${locationId}/provider-connection-statuses`
  );

export const getLocationFooterPrintingMessage = (locationId: string | number) =>
  api.get<ResponseType<PrintingSettingsResponse>>(
    `/v1/locations/${locationId}/footer-message`
  );

export const updatePrintingFooterMessage = (
  locationId: string | number,
  message: string | null
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/footer-messagge`, {
    message,
  });

export const getAllMenuProviders = () =>
  api.get<ResponseType<Array<MenuSignInPropertyEntity>>>(
    `/v1/locations/menu-providers`
  );

export const createMenuProvider = (data: CreateMenuProvider) =>
  api.post<ResponseType<MenuSignInPropertyEntity>>(
    `/v1/locations/menu-providers`,
    data
  );

export const deleteMenuProvider = (menuId: string) =>
  api.delete<ResponseType<null>>(`/v1/locations/${menuId}`);

export const getLocationById = (
  locationId: string | number,
  params: DateRangeParams
) =>
  api.get<ResponseType<LocationEntity>>(`/v1/locations/${locationId}`, {
    params,
  });

export const getLocationsByIds = (locationIds: number[]) =>
  api.post<ResponseType<Array<SimpleLocationEntity>>>(
    `/v1/locations/verified-by-ids`,
    {
      locationIds,
    }
  );

export const updateLocationById = (
  locationId: string | number,
  data: UpdateLocationRequestData
) => api.put<ResponseType<null>>(`/v1/locations/${locationId}`, data);

export const updateLocationByIdV2 = (
  locationId: string | number,
  data: FormData
) =>
  api.put<ResponseType<null>>(`/v2/locations/${locationId}`, data, {
    headers: { 'content-type': 'multipart/form-data' },
  });

/**
 * The location's Google My Business review link, offered on the tracking page
 * after a 4-5 star rating on orders from the location's own brand. `null`
 * clears it; anything else must start with http(s):// or it is a 400.
 */
export const updateLocationGoogleBusinessUrl = (
  locationId: string | number,
  googleBusinessUrl: string | null
) =>
  api.put<ResponseType<null>>(
    `/v1/locations/${locationId}/google-business-url`,
    { googleBusinessUrl }
  );

/** Payout config for every service. Always returns one entry per service. */
export const getLocationPayoutConfig = (locationId: string | number) =>
  api.get<ResponseType<PayoutConfigResponse[]>>(
    `/v1/locations/${locationId}/payout-config`
  );

/**
 * Admin only. Services left out of `configs` keep their current config, and
 * the write is atomic - one invalid config rejects the whole request.
 */
export const updateLocationPayoutConfig = (
  locationId: string | number,
  data: UpdatePayoutConfigsRequestData
) =>
  api.put<ResponseType<PayoutConfigResponse[]>>(
    `/v1/locations/${locationId}/payout-config`,
    data
  );

export const connectLocationWithProvider = (
  locationId: string | number,
  data: ConnectProviderRequestData
) =>
  api.post<ResponseType<string>>(
    `/v1/locations/${locationId}/provider/connect`,
    data
  );

export const transferToGrubhubPos = (
  locationId: string | number,
  provider: Provider
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/provider/transfer`,
    {
      provider,
    }
  );

export const updateStoreLink = (
  providerId: string | number,
  storeLink: string
) =>
  api.put<ResponseType<null>>(`/v1/providers/${providerId}/store-link`, {
    storeLink,
  });

/** `confirmUnpaidOrders` only after the user has seen the orders in the
 *  409 and agreed to pay them out from the subtotal - never up front. */
export const disconnectLocationWithProvider = (
  locationId: string | number,
  provider: Provider,
  confirmUnpaidOrders?: boolean
) =>
  api.post<ResponseType<string>>(
    `/v1/locations/${locationId}/provider/disconnect`,
    { provider, ...(confirmUnpaidOrders && { confirmUnpaidOrders: true }) }
  );

export const setProviderCredentials = (
  locationId: string | number,
  data: SetLocationCredentialsRequestData
) => api.post(`/v1/locations/${locationId}/provider/credentials`, data);

export const getLocationsProviderConfig = (
  locationId: string | number,
  provider: Provider
) =>
  api.get<ResponseType<BrandProviderConfigEntity>>(
    `/v1/locations/${locationId}/provider/config`,
    {
      params: {
        provider,
      },
    }
  );

export const getLocationsProviderConfigs = (locationId: string | number) =>
  api.get<ResponseType<Array<BrandProviderConfigEntity>>>(
    `/v1/locations/${locationId}/provider/configs`
  );

export const getLocationsProviderStatus = (
  locationId: string | number,
  provider: Provider
) =>
  api.get<ResponseType<{ paused: boolean }>>(
    `/v1/locations/${locationId}/provider/status`,
    {
      params: {
        provider,
      },
    }
  );

export const pauseProvider = (
  locationId: string | number,
  data: { provider: Provider } & ProviderPauseLength
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/provider/pause`,
    data
  );

export const unpauseProvider = (
  locationId: string | number,
  provider: Provider
) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/provider/unpause`, {
    provider,
  });

export const pauseAllProviders = (
  locationId: string | number,
  length: ProviderPauseLength
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/provider/pause-all`,
    length
  );

export const pauseAllProvidersBrands = (
  brandId: string | number,
  minutes: number | null
) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/pause-all`, {
    minutes,
  });

export const unpauseAllProviders = (locationId: string | number) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/provider/unpause-all`
  );

export const unpauseAllProvidersBrands = (brandId: string | number) =>
  api.post<ResponseType<null>>(`/v1/brands/${brandId}/provider/unpause-all`);

export const syncLocationProviders = (locationId: string | number) =>
  api.post<ResponseType<null>>(`/v1/locations/sync-providers`, {
    locationId,
  });

export const syncLocationStores = (locationId: string | number) =>
  api.post<ResponseType<null>>(`/v1/locations/sync-stores`, {
    locationId,
  });

/** The location's AND its brands' pauses that have not started yet. */
export const getScheduledPauses = (locationId: string | number) =>
  api.get<ResponseType<ScheduledPause[]>>(
    `/v1/locations/${locationId}/provider/scheduled-pauses`
  );

/** `locationId` is the location's even for a virtual brand's pause. */
export const cancelScheduledPause = (
  locationId: string | number,
  pauseId: number
) =>
  api.delete<ResponseType<null>>(
    `/v1/locations/${locationId}/provider/scheduled-pauses/${pauseId}`
  );

export const getAllLocationsProvidersPaused = (locationId: string | number) =>
  api.get<ResponseType<{ paused: boolean }>>(
    `/v1/locations/${locationId}/provider/paused-all`
  );

export const updateLocationNotes = (
  locationId: string | number,
  notes: string
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/notes`, { notes });

export const sendLocationTrackingSms = (
  locationId: string | number,
  data: { trackingLink: string; type: TrackingSmsType }
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/tracking-sms`,
    data
  );

export const getLocationTrackingSms = (locationId: string | number) =>
  api.get<ResponseType<LocationTrackingSmsEntity[]>>(
    `/v1/locations/${locationId}/tracking-sms`
  );

export const parseGooglePlace = (placeId: string | number) =>
  api.get<ResponseType<IncompleteLocation>>(
    `/v1/locations/${placeId}/parse-google-place`
  );

export const updateLocationAddress = (
  locationId: string | number,
  data: CreateCompleteLocation
) => api.put<ResponseType<null>>(`/v1/locations/${locationId}/address`, data);

export const upsertLocationVDriveProviderContact = (
  locationId: string | number | null,
  data: UpsertProviderContactRequestData
) =>
  api.put<ResponseType<null>>(
    `/v1/locations/${locationId}/provider-contract`,
    data
  );

export const deleteLocationVDriveProvider = (
  locationId: string | number,
  provider: Provider
) =>
  api.delete<ResponseType<null>>(
    `/v1/locations/${locationId}/provider-contract/${provider}`
  );

export const updateLocationOwnership = (
  locationId: string | number,
  data: UpdateLocationOwnershipRequestData
) => api.put<ResponseType<null>>(`/v1/locations/${locationId}/ownership`, data);

export const updateLocationConfig = (
  locationId: string | number,
  data: UpdateLocationConfigRequestData
) =>
  api.put<ResponseType<null>>(
    `/v1/locations/${locationId}/configuration`,
    data
  );

export const getLocationEarningPlan = (locationId: string | number) =>
  api.get<ResponseType<LocationEarningPlan>>(
    `/v1/locations/${locationId}/earning-plan`
  );

export const updateLocationReferralAgentEarningPlan = (
  locationId: string | number,
  data: UpdateLocationEarningsPlanRequestData
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/earning-plan`, data);

export const getLocationAgentsEarningPlans = (locationId: string | number) =>
  api.get<ResponseType<LocationAgentsEarningPlans>>(
    `/v1/locations/${locationId}/agents/earning-plans`
  );

export const getLocationAgents = (locationId: string | number) =>
  api.get<ResponseType<LocationAgent[]>>(`/v1/locations/${locationId}/agents`);

export const addLocationAgent = (
  locationId: string | number,
  agentId: number
) =>
  api.post<ResponseType<null>>(`/v1/locations/${locationId}/agents`, {
    agentId,
  });

export const deleteLocationAgent = (
  locationId: string | number,
  agentId: number
) =>
  api.delete<ResponseType<null>>(
    `/v1/locations/${locationId}/agents/${agentId}`
  );

export const addLocationAgentToService = (
  locationId: string | number,
  agentId: number,
  service: LocationAgentServiceKey
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/agents/${agentId}/services/${service}`
  );

export const deleteLocationAgentFromService = (
  locationId: string | number,
  agentId: number,
  service: LocationAgentServiceKey
) =>
  api.delete<ResponseType<null>>(
    `/v1/locations/${locationId}/agents/${agentId}/services/${service}`
  );

export const updateLocationAgentShares = (
  locationId: string | number,
  data: SaveLocationAgentSharesRequestData
) =>
  api.put<ResponseType<null>>(
    `/v1/locations/${locationId}/agents/shares`,
    data
  );

export const updateLocationReferralVOrdersAgentEarningPlan = (
  locationId: string | number,
  amount: number
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/vorders-plan`, {
    amount,
  });

export const updateLocationReferralVDriveAgentEarningPlan = (
  locationId: string | number,
  amount: number
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/vdrive-plan`, {
    amount,
  });

export const upsertLocationSalesConfig = (
  locationId: string | number,
  data: UpdateLocationSalesRequestData
) =>
  api.put<ResponseType<null>>(`/v1/locations/${locationId}/sales-config`, data);

/**
 * How much to charge per order for the vTablet fee to add up to `target`
 * (cents, 1-100000), the monthly fee picked in the form whether saved or not.
 * Left out, the backend uses the saved monthly fee, else $89. Takes a few
 * seconds (the AI writes the explanation) and is never cached server side:
 * every call is a fresh suggestion.
 */
export const getPerOrderFeeSuggestion = (
  locationId: string | number,
  target?: number
) =>
  api.get<ResponseType<ServiceFeeSuggestion>>(
    `/v1/locations/${locationId}/sales-config/per-order-fee-suggestion`,
    { params: target ? { target } : undefined }
  );

export const getLocationHolidayHours = (locationId: string | number) =>
  api.get<ResponseType<Array<HolidayHoursReponseV2>>>(
    `/v2/locations/${locationId}/holiday-hours`
  );

export const updateLocationsHolidayHours = (
  locationId: string | number,
  data: UpdateHolidayHoursLocationV2Dto
) =>
  api.post<ResponseType<Array<HolidayHoursReponseV2>>>(
    `/v2/locations/${locationId}/holiday-hours`,
    data
  );

export const deleteLocationHolidayHours = (
  locationId: string | number,
  data: {
    date: string;
    provider: Provider;
  }
) =>
  api.delete<ResponseType<null>>(`/v1/locations/${locationId}/holiday-hours`, {
    data,
  });

export const getLocationsWorkingHours = (locationId: string | number) =>
  api.get<ResponseType<WorkingHoursReponseV2>>(
    `/v2/locations/${locationId}/working-hours`
  );

export const updateLocationsWorkingHours = (
  locationId: string | number,
  data: UpdateWorkingHoursLocationV2Dto
) =>
  api.post<ResponseType<WorkingHoursReponseV2>>(
    `/v2/locations/${locationId}/working-hours`,
    data
  );

export const createLocationWorkingHours = (
  locationId: string | number,
  data: CreateWorkingHoursRequestData
) =>
  api.post<ResponseType<null>>(
    `/v1/locations/${locationId}/working-hours`,
    data
  );

export const deleteLocationWorkingHours = (
  locationId: string | number,
  data: {
    provider: Provider;
    id: string;
  }
) =>
  api.delete<ResponseType<null>>(`/v1/locations/${locationId}/working-hours`, {
    data,
  });

export const createStaffMember = (
  locationId: string | number,
  data: CreateStaffMemberRequestData
) => api.post<ResponseType<null>>(`/v1/locations/${locationId}/staff`, data);

export const deleteLocationUserReferral = (locationId: string | number) =>
  api.delete<ResponseType<null>>(`/v1/locations/${locationId}/referral`);

export const migrateOLE = () =>
  api.patch<ResponseType<null>>('/v1/locations/migrate-ole');

export const getLocationsOverview = (
  id: string | number,
  params: GetLocationsOverviewParams
) =>
  api.get<ResponseType<OrdersOverviewEntity>>(
    `/v2/locations/${id}/orders/overview`,
    {
      params,
    }
  );

export const getCustomerStats = () =>
  api.get<ResponseType<AgentCustomerStatsResponse>>(
    `/v1/locations/customer-stats`
  );

export const getCustomerStatsVDrive = (params: PaginationParams) =>
  api.get<ResponseType<AgentCustomersStatsVDriveResponse[]>>(
    `/v1/locations/customer-stats/vdrive`,
    {
      params,
    }
  );

export const getCustomerStatsVGrubs = (params: PaginationParams) =>
  api.get<ResponseType<AgentCustomersStatsVGrubsResponse[]>>(
    `/v1/locations/customer-stats/vgrubs`,
    {
      params,
    }
  );

export const getCustomerStatsRecovery = (params: PaginationParams) =>
  api.get<ResponseType<AgentCustomersStatsRecoveryResponse[]>>(
    `/v1/locations/customer-stats/recovery`,
    {
      params,
    }
  );

export const updateLocationProfileImage = (
  locationId: string | number,
  data: FormData
) =>
  api.post<ResponseType<string>>(`/v1/locations/${locationId}/image`, data, {
    headers: { 'content-type': 'multipart/form-data' },
  });

export const getActiveBrandsV2 = (locationId: string | number) =>
  api.get<ResponseType<ActiveBrandEntity[]>>(
    `/v2/locations/${locationId}/brands/active`
  );

/** The location's own brands: no vGrubs virtual brands, no physical
 * location brand. Partner / manager only. */
export const getOwnBrandsV2 = (locationId: string | number) =>
  api.get<ResponseType<OwnBrandEntity[]>>(
    `/v2/locations/${locationId}/brands/own`
  );

export const getLocationPosStatuses = (
  locationId: string | number,
  config?: import('axios').AxiosRequestConfig
) =>
  api.get<ResponseType<PosStatusResponse>>(
    `/v2/locations/${locationId}/pos-statuses`,
    config
  );

export const connectLocationWithPos = (
  locationId: string | number,
  data: ConnectPOSDto
) =>
  api.post<ResponseType<string>>(
    `/v1/locations/${locationId}/pos/connect`,
    data
  );

/** Fresh POS Hub link for an already connected POS. The token in it is
 * short lived, so call this on click and open the result right away. */
export const getPosIntegrationLink = (
  locationId: string | number,
  posType: PosType
) =>
  api.get<ResponseType<string>>(
    `/v1/locations/${locationId}/pos/integration-link`,
    { params: { posType } }
  );

export const disconnectLocationWithPos = (
  locationId: string | number,
  provider: PosType,
  switchToTabletMode: boolean
) =>
  api.post<ResponseType<string>>(`/v1/locations/${locationId}/pos/disconnect`, {
    posType: provider,
    switchToTabletMode,
  });
